import type * as ngMatCore from '@angular/material/core'
import * as ngMatDatepicker from '@angular/material/datepicker'
import * as ents from '@attain-apps/entities'
import { Range } from '@attain-apps/range'
import { TypeChecker } from '@attain-apps/type-guards'

export type DateRange = ngMatDatepicker.DateRange<Date>

export namespace DateRange {
  /** Normalizes both endpoints to local midnight for Angular Material. */
  export function normalizeToLocal(
    range: Readonly<Range<Date>> | undefined,
  ): Range<Date> | undefined {
    return _normalize(range, ents.core.display.date.normalizeToLocal)
  }

  /** Normalizes both endpoints to UTC midnight for emitted date-only values. */
  export function normalizeToUtc(
    range: Readonly<Range<Date>> | undefined,
  ): Range<Date> | undefined {
    return _normalize(range, ents.core.display.date.normalizeToUtc)
  }

  /** Rebuilds a range using the requested endpoint normalization. */
  function _normalize(
    range: Readonly<Range<Date>> | undefined,
    normalizeEdge: (value: unknown) => Date | undefined,
  ): Range<Date> | undefined {
    if (!range) {
      return
    }

    return new Range<Date>({
      end: normalizeEdge(range.end),
      includesEnd: range.includesEnd,
      includesStart: range.includesStart,
      start: normalizeEdge(range.start),
    })
  }

  /**
   * Factory method for creating a DateRange instance.
   *
   */
  // NOTE Uses `null` (not `undefined`) because Angular Material expects `null` for empty dates.
  export function generate(params?: { start?: Edge; end?: Edge }) {
    return new ngMatDatepicker.DateRange(
      params?.start ?? null,
      params?.end ?? null,
    )
  }

  /**
   * Calculates a new date range based on a given {@link Duration} and the last
   * modified edge.
   */
  export function calculate(
    context: { adapter: ngMatCore.DateAdapter<Date> },
    params: {
      duration: DateRange.Duration
      lastChanged?: 'start' | 'end'
      currentDateRange: DateRange
    },
  ): DateRange | undefined {
    const { lastChanged, currentDateRange, duration } = params

    // If no duration is provided, generate an open-ended (infinite) date range.
    if (TypeChecker.isNullish(duration.length)) {
      return DateRange.generate()
    }

    const { start: currentStart, end: currentEnd } = currentDateRange

    let newStart = currentStart
    let newEnd = currentEnd

    if (lastChanged === 'start' && currentStart) {
      // User changed start → calculate end by adding duration to start
      newEnd = DateRange.Edge.calculate(context, {
        date: currentStart,
        duration,
        durationOrigin: 'start',
      })
    } else if (lastChanged === 'end' && currentEnd) {
      // User changed end → calculate start by subtracting duration from end
      newStart = DateRange.Edge.calculate(context, {
        date: currentEnd,
        duration,
        durationOrigin: 'end',
      })
    } else if (currentStart) {
      // Fallback: assume start is given
      newEnd = DateRange.Edge.calculate(context, {
        date: currentStart,
        duration,
        durationOrigin: 'start',
      })
    } else if (currentEnd) {
      // Fallback: assume end is given
      newStart = DateRange.Edge.calculate(context, {
        date: currentEnd,
        duration,
        durationOrigin: 'end',
      })
    } else {
      // No known reference point to apply duration
      return
    }

    // If duration is negative, reverse the start and end to preserve order
    return duration.length < 0
      ? DateRange.generate({ end: newStart, start: newEnd })
      : DateRange.generate({ end: newEnd, start: newStart })
  }

  export interface Duration {
    length?: number
    unit: Duration.Unit
  }
  export namespace Duration {
    /**
     * Calculates the duration between two dates in the most appropriate unit
     * (years, quarters, months, weeks, or days), depending on how cleanly the difference fits.
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function calculate(
      context: { adapter: ngMatCore.DateAdapter<Date> },
      params: {
        range: DateRange
      },
    ): Duration | null {
      const { range } = params
      const { adapter } = context

      if (!range.start || !range.end) {
        return null
      }

      const start = range.start

      // Make end inclusive
      const end = adapter.addCalendarDays(range.end, 1)

      // Calculate month difference
      const years = adapter.getYear(end) - adapter.getYear(start)
      const startMonth = adapter.getMonth(start)
      const endMonth = adapter.getMonth(end)
      const totalMonths = years * 12 + (endMonth - startMonth)

      // Check if duration is in whole years
      if (
        totalMonths % 12 === 0 &&
        adapter.sameDate(adapter.addCalendarYears(start, totalMonths / 12), end)
      ) {
        return {
          length: totalMonths / 12,
          unit: Unit.Years,
        }
      }

      // Check if duration is in whole quarters (3-month chunks)
      if (
        totalMonths % 3 === 0 &&
        adapter.sameDate(adapter.addCalendarMonths(start, totalMonths), end)
      ) {
        return {
          length: totalMonths / 3,
          unit: Unit.Quarters,
        }
      }

      if (
        totalMonths > 0 &&
        adapter.sameDate(adapter.addCalendarMonths(start, totalMonths), end)
      ) {
        return { length: totalMonths, unit: Unit.Months }
      }

      // Calculate difference in full days
      const totalDays = Math.round(
        (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
      )

      // Check if duration is in whole weeks
      if (totalDays % 7 === 0) {
        return { length: totalDays / 7, unit: Unit.Weeks }
      }

      // Default to duration in days
      return { length: totalDays, unit: Unit.Days }
    }

    export enum Unit {
      Years = 'Years',
      Quarters = 'Quarters',
      Months = 'Months',
      Weeks = 'Weeks',
      Days = 'Days',
    }

    export namespace Unit {
      const UNITS: ReadonlySet<Unit> = new Set(Object.values(Unit) as Unit[])

      export const ALL = Array.from(UNITS) as readonly Unit[]
      export const isUnit = TypeChecker.Factory.oneOf(UNITS)
    }
  }

  /**
   * `null` is used to explicitly indicate no date is selected.
   * Angular Material expects `null` for empty date values, ensuring compatibility.
   */
  export type Edge = Date | null
  export namespace Edge {
    /**
     * Parses a date string into a valid {@link Edge} object.
     */
    export function parseFrom(value: string): Edge {
      // Expecting format M/d/yyyy
      const parts = value.trim().split('/')

      if (parts.length !== 3) {
        return null
      }

      const [monthStr, dayStr, yearStr] = parts

      const month = parseInt(monthStr, 10) - 1 // JS months are 0-based
      const day = parseInt(dayStr, 10)
      const year = parseInt(yearStr, 10)

      if (
        isNaN(month) ||
        isNaN(day) ||
        isNaN(year) ||
        month < 0 ||
        month > 11 ||
        day < 1 ||
        day > 31
      ) {
        return null
      }

      const date = new Date(year, month, day)

      // Validate that the created date matches the input
      if (
        date.getFullYear() !== year ||
        date.getMonth() !== month ||
        date.getDate() !== day
      ) {
        return null
      }

      return date
    }

    /**
     * Computes a new date by applying a duration to a reference date.
     *
     * The calculation is based on a `duration` object (with a numeric `length` and a `unit`)
     * and a reference `date`. The `durationOrigin` flag indicates whether the duration
     * should be applied starting from the `start` or the `end` of the range.
     *
     * @example
     *   From Start:
     *     Start = July 1, Duration = 3 days
     *     → July 1 + 3 days = July 4 → Adjusted: July 3
     *
     *   From End:
     *     End = July 4, Duration = 3 days
     *     → July 4 - 3 days = July 1 → Adjusted: July 2
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function calculate(
      context: {
        adapter: ngMatCore.DateAdapter<Date>
      },
      params: {
        date: Date
        duration: Duration
        durationOrigin: 'start' | 'end'
      },
    ): Edge {
      const { adapter } = context
      const { date, duration, durationOrigin } = params

      if (!duration.length || !duration.unit) {
        return null
      }

      // Apply a sign to the duration based on the origin:
      // → 'start' means moving forward (positive length),
      // → 'end' means moving backward (negative length).
      const length =
        durationOrigin === 'start' ? duration.length : -duration.length

      let result: Date

      switch (duration.unit) {
        case Duration.Unit.Days:
          result = adapter.addCalendarDays(date, length)
          break
        case Duration.Unit.Weeks:
          result = adapter.addCalendarDays(date, length * 7)
          break
        case Duration.Unit.Months:
          result = adapter.addCalendarMonths(date, length)
          break
        case Duration.Unit.Quarters:
          result = adapter.addCalendarMonths(date, length * 3)
          break
        case Duration.Unit.Years:
          result = adapter.addCalendarYears(date, length)
          break
      }

      // Since the end date should be included, adjust the result by one day:
      // - Subtract one day if moving forward in time (positive duration),
      // - Add one day if moving backward (negative duration).
      return adapter.addCalendarDays(result, length > 0 ? -1 : 1)
    }
  }

  export enum Predefined {
    MonthToDate = 'MTD',
    QuarterToDate = 'QTD',
    YearToDate = 'YTD',
    ThisMonth = 'This Month',
    ThisQuarter = 'This Quarter',
    ThisYear = 'This Year',
    PreviousMonth = 'Previous Month',
    PreviousQuarter = 'Previous Quarter',
    PreviousYear = 'Previous Year',
  }

  const PREDEFINED: ReadonlySet<Predefined> = new Set(
    Object.values(Predefined) as Predefined[],
  )

  export namespace Predefined {
    export const ALL = Array.from(PREDEFINED) as readonly Predefined[]
    /** Calculates {@link DateRange} based on the predefined options . */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function calculate(
      rangeName: Predefined,
      adapter: ngMatCore.DateAdapter<Date>,
    ): DateRange {
      const today = adapter.today()
      switch (rangeName) {
        case Predefined.MonthToDate:
          return _calculateMonthToDate(today, adapter)

        case Predefined.QuarterToDate:
          return _calculateQuarterToDate(today, adapter)

        case Predefined.YearToDate:
          return _calculateYearToDate(today, adapter)

        case Predefined.ThisMonth:
          return _calculateMonth(today, adapter)

        case Predefined.ThisQuarter:
          return _calculateQuarter(today, adapter)

        case Predefined.ThisYear:
          return _calculateYear(today, adapter)

        case Predefined.PreviousMonth: {
          const prevMonthDate = adapter.addCalendarMonths(today, -1)
          return _calculateMonth(prevMonthDate, adapter)
        }

        case Predefined.PreviousQuarter: {
          const prevQuarterDate = adapter.addCalendarMonths(today, -3)
          return _calculateQuarter(prevQuarterDate, adapter)
        }

        case Predefined.PreviousYear: {
          const prevYearDate = adapter.addCalendarYears(today, -1)
          return _calculateYear(prevYearDate, adapter)
        }
      }
    }
  }
}

/** Full month */
function _calculateMonth(
  forDay: Date,
  adapter: ngMatCore.DateAdapter<Date>,
): DateRange {
  const year = adapter.getYear(forDay)
  const month = adapter.getMonth(forDay)
  const start = adapter.createDate(year, month, 1)
  const end = adapter.addCalendarDays(
    start,
    adapter.getNumDaysInMonth(forDay) - 1,
  )
  return DateRange.generate({ end, start })
}

/** Month To Date */
function _calculateMonthToDate(
  today: Date,
  adapter: ngMatCore.DateAdapter<Date>,
): DateRange {
  const year = adapter.getYear(today)
  const month = adapter.getMonth(today)
  const start = adapter.createDate(year, month, 1)
  return DateRange.generate({ end: today, start })
}

/** Full quarter */
function _calculateQuarter(
  forDay: Date,
  adapter: ngMatCore.DateAdapter<Date>,
): DateRange {
  const year = adapter.getYear(forDay)
  const month = adapter.getMonth(forDay)
  const quarterStartMonth = Math.floor(month / 3) * 3
  const start = adapter.createDate(year, quarterStartMonth, 1)
  // Calculate quarter end: add 3 months, minus 1 day
  const quarterEnd = adapter.addCalendarMonths(start, 3)
  const end = adapter.addCalendarDays(quarterEnd, -1)
  return DateRange.generate({ end, start })
}

/** Quarter To Date */
function _calculateQuarterToDate(
  today: Date,
  adapter: ngMatCore.DateAdapter<Date>,
): DateRange {
  const year = adapter.getYear(today)
  const month = adapter.getMonth(today)
  const quarterStartMonth = Math.floor(month / 3) * 3
  const start = adapter.createDate(year, quarterStartMonth, 1)
  return DateRange.generate({ end: today, start })
}

/** Full year */
function _calculateYear(
  forDay: Date,
  adapter: ngMatCore.DateAdapter<Date>,
): DateRange {
  const year = adapter.getYear(forDay)
  const start = adapter.createDate(year, 0, 1) // Jan 1st
  const end = adapter.createDate(year, 11, 31) // Dec 31st
  return DateRange.generate({ end, start })
}

/** Year To Date */
function _calculateYearToDate(
  today: Date,
  adapter: ngMatCore.DateAdapter<Date>,
): DateRange {
  const year = adapter.getYear(today)
  const start = adapter.createDate(year, 0, 1)
  return DateRange.generate({ end: today, start })
}
